import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Check, X } from "lucide-react";

export function RolesMatrixTab() {
  const [cat, setCat] = useState(null);

  useEffect(() => { api.get("/org/rbac/catalogue").then((r) => setCat(r.data)); }, []);

  if (!cat) return <div className="h-64 bg-white border border-slate-200 rounded-lg shimmer" />;

  const { roles, modules, actions, matrix } = cat;

  return (
    <>
      {/* Roles list */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {roles.map((r) => (
          <div key={r.code} data-testid={`role-card-${r.code}`} className="bg-white border border-slate-200 rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-display text-base font-semibold text-slate-900">{r.name}</div>
                <div className="text-[11px] uppercase tracking-widest text-slate-400 mt-0.5">{r.scope}</div>
              </div>
              <span className="text-[10px] font-mono text-slate-400">{r.code}</span>
            </div>
            <p className="text-xs text-slate-600 mt-3">{r.description}</p>
          </div>
        ))}
      </div>

      {/* Matrix table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100">
          <h3 className="font-display text-lg font-semibold text-slate-900">Permission matrix</h3>
          <p className="text-xs text-slate-500">Modules × Roles · dot shows the actions each role can perform</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="text-left font-medium px-4 py-3 text-slate-500 uppercase tracking-wider sticky left-0 bg-slate-50 z-10 min-w-[220px]">Module</th>
                {roles.map((r) => (
                  <th key={r.code} className="text-left font-medium px-2 py-3 text-slate-500 uppercase tracking-wider text-[10px]">{r.name.split(" ")[0]}<br />{r.name.split(" ").slice(1).join(" ") || "\u00A0"}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {modules.map((m) => (
                <tr key={m.code} className="border-b border-slate-100 hover:bg-slate-50/60">
                  <td className="px-4 py-2.5 sticky left-0 bg-white z-10 border-r border-slate-100">
                    <div className="font-medium text-slate-800">{m.name}</div>
                    <div className="text-[10px] text-slate-400">Phase {m.phase}</div>
                  </td>
                  {roles.map((r) => {
                    const acts = matrix[r.code]?.[m.code] || [];
                    return (
                      <td key={r.code} className="px-2 py-2.5 align-middle">
                        {acts.length === 0 ? (
                          <span className="inline-flex items-center gap-1 text-slate-300 text-[10px]"><X className="w-3 h-3" /> —</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {actions.map((a) => acts.includes(a.code) ? (
                              <span key={a.code} title={a.description}
                                    className="inline-flex items-center px-1.5 py-0.5 rounded bg-gbe/10 text-gbe text-[9px] font-medium uppercase tracking-wider">
                                {a.code.slice(0, 3)}
                              </span>
                            ) : null)}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-4 text-xs text-slate-500 flex flex-wrap items-center gap-3">
        <span>Legend:</span>
        {actions.map((a) => (
          <span key={a.code} className="inline-flex items-center gap-1">
            <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-gbe/10 text-gbe text-[9px] font-medium uppercase">{a.code.slice(0,3)}</span>
            {a.name}
          </span>
        ))}
      </div>
    </>
  );
}
